import * as React from "react";
import * as AvatarPrimitive from "@radix-ui/react-avatar";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../utils/cn";

const avatarVariants = cva("relative flex shrink-0 overflow-hidden rounded-full", {
  variants: {
    size: {
      sm: "size-7 text-xs",
      md: "size-9 text-sm",
      lg: "size-12 text-base",
    },
  },
  defaultVariants: {
    size: "md",
  },
});

export interface AvatarProps
  extends Omit<React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root>, "children">,
    VariantProps<typeof avatarVariants> {
  image?: string | null;
  name?: string | null;
  fallback?: string;
  alt?: string;
}

function getInitials(name?: string | null) {
  return (name ?? "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");
}

const Avatar = React.forwardRef<React.ElementRef<typeof AvatarPrimitive.Root>, AvatarProps>(
  ({ className, image, name, fallback, alt, size, ...props }, ref) => (
    <AvatarPrimitive.Root ref={ref} className={cn(avatarVariants({ size }), className)} {...props}>
      {image ? <AvatarPrimitive.Image src={image} alt={alt ?? name ?? ""} className="aspect-square size-full" /> : null}
      <AvatarPrimitive.Fallback className="flex size-full items-center justify-center rounded-full bg-muted font-medium text-muted-foreground">
        {fallback ?? getInitials(name) ?? "?"}
      </AvatarPrimitive.Fallback>
    </AvatarPrimitive.Root>
  ),
);
Avatar.displayName = "Avatar";

export { Avatar, avatarVariants };
